<template>
  <div class="wixtar-panel">
    <div
      v-if="clientMemberSystemType === 'LOCKLINE'"
      class="alert alert-warning"
    >
      <div class="alert-inner">
        <i class="ri-error-warning-line"></i>
        <div>
          <strong>此客戶使用 LOCKLINE 會員系統</strong>
          <p>
            此客戶目前使用 LOCKLINE 會員系統，無法設定 Wixtar 整合。如需使用 Wixtar 整合，請先將客戶的會員系統類型設定為 WIXTAR。
          </p>
        </div>
      </div>
    </div>

    <template v-if="clientMemberSystemType !== 'LOCKLINE'">
      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
        <p>載入中...</p>
      </div>

      <div v-else-if="error" class="error-state">
        <p class="error-message">{{ error }}</p>
        <button class="btn btn-secondary" @click="fetchConfig">重試</button>
      </div>

      <template v-else>
        <div class="settings-card">
          <div class="settings-card-header">
            <i class="ri-terminal-box-line"></i>
            <div>
              <h2 class="settings-card-title">Wixtar 全通路整合</h2>
              <p class="settings-card-desc">設定 Wixtar API 認證、同步選項與查看同步狀態</p>
            </div>
            <div class="card-header-actions">
              <button
                v-if="!config"
                class="btn btn-primary"
                @click="handleCreateConfig"
              >
                <i class="ri-add-line"></i>
                設定 Wixtar 整合
              </button>
              <button
                v-else
                class="btn btn-secondary"
                @click="handleEditConfig"
              >
                <i class="ri-edit-line"></i>
                編輯設定
              </button>
            </div>
          </div>

          <div v-if="!config" class="empty-state">
            <div class="empty-content">
              <i class="ri-terminal-box-line empty-icon"></i>
              <h3>尚未設定 Wixtar 整合</h3>
              <p class="text-muted">請點擊上方按鈕開始設定 Wixtar 全通路系統整合</p>
            </div>
          </div>

          <div v-else class="card-body">
            <div class="status-row">
              <span class="info-label">整合狀態</span>
              <span :class="config.isActive ? 'badge badge-success' : 'badge badge-secondary'">
                {{ config.isActive ? '已啟用' : '未啟用' }}
              </span>
            </div>
            <div class="info-grid">
              <div class="info-item">
                <span class="info-label">API URL</span>
                <span class="info-value">{{ config.apiUrl || '-' }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Wixtar Client ID (authkey)</span>
                <span class="info-value">{{ config.wixtarClientId || '-' }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Wixtar Client Secret (acckey)</span>
                <span class="info-value">{{ config.wixtarClientSecret || '-' }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">API 版本</span>
                <span class="info-value">{{ config.apiVersion || 'v2' }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">同步會員</span>
                <span class="info-value">
                  <i :class="config.syncMembers ? 'ri-checkbox-circle-fill text-success' : 'ri-close-circle-fill text-danger'"></i>
                  {{ config.syncMembers ? '已啟用' : '未啟用' }}
                </span>
              </div>
              <div class="info-item">
                <span class="info-label">同步訂單</span>
                <span class="info-value">
                  <i :class="config.syncOrders ? 'ri-checkbox-circle-fill text-success' : 'ri-close-circle-fill text-danger'"></i>
                  {{ config.syncOrders ? '已啟用' : '未啟用' }}
                </span>
              </div>
              <div class="info-item">
                <span class="info-label">同步點數</span>
                <span class="info-value">
                  <i :class="config.syncPoints ? 'ri-checkbox-circle-fill text-success' : 'ri-close-circle-fill text-danger'"></i>
                  {{ config.syncPoints ? '已啟用' : '未啟用' }}
                </span>
              </div>
              <div class="info-item">
                <span class="info-label">同步票券</span>
                <span class="info-value">
                  <i :class="config.syncTickets ? 'ri-checkbox-circle-fill text-success' : 'ri-close-circle-fill text-danger'"></i>
                  {{ config.syncTickets ? '已啟用' : '未啟用' }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div v-if="config" class="settings-card">
          <div class="settings-card-header">
            <i class="ri-webhook-line"></i>
            <div>
              <h2 class="settings-card-title">交易事件觸發</h2>
              <p class="settings-card-desc">
                控制 Wixtar 訂單 webhook 是否驅動本系統升等禮與集章。POS 與 Lockline 商城單都會先到 Wixtar，再統一由此事件觸發；商城本機完成流程不會發章／升等。
              </p>
            </div>
          </div>

          <div class="card-body">
            <div v-if="!canEnableTransactionTriggers" class="field-block webhook-prereq-warn">
              <p class="field-hint">
                <i class="ri-error-warning-line"></i>
                目前
                <template v-if="!config.isActive">整合未啟用</template>
                <template v-if="!config.isActive && config.syncOrders === false">，且</template>
                <template v-if="config.syncOrders === false">「同步訂單」已關閉</template>
                。請先到上方編輯設定開啟後，才能啟用下方升等／集章開關。
              </p>
            </div>

            <div class="field-block">
              <div class="toggle-row">
                <label class="toggle-switch" :class="{ disabled: !canEnableTransactionTriggers }">
                  <input
                    type="checkbox"
                    v-model="transactionTriggers.wixtarTransactionTierUpgradeEnabled"
                    :disabled="!canEnableTransactionTriggers"
                  />
                  <span class="slider"></span>
                </label>
                <span class="toggle-label">Wixtar 交易觸發會員升等</span>
              </div>
              <p class="field-hint">
                開啟後，訂單 webhook 同步為「已完成且已付款」時會重算等級並可觸發升等禮；不會再發 CRM 消費點數。
              </p>
            </div>

            <div class="field-block">
              <div class="toggle-row">
                <label class="toggle-switch" :class="{ disabled: !canEnableTransactionTriggers }">
                  <input
                    type="checkbox"
                    v-model="transactionTriggers.wixtarTransactionStampIssueEnabled"
                    :disabled="!canEnableTransactionTriggers"
                  />
                  <span class="slider"></span>
                </label>
                <span class="toggle-label">Wixtar 交易觸發集章</span>
              </div>
              <p class="field-hint">
                開啟後會依集章活動交易規則自動發章；關閉時仍可用 QR／人工補章。
              </p>
            </div>

            <div class="field-actions">
              <button class="btn btn-primary" :disabled="transactionTriggerSaving" @click="handleSaveTransactionTriggers">
                <i class="ri-save-line"></i>
                {{ transactionTriggerSaving ? '儲存中...' : '儲存設定' }}
              </button>
              <span v-if="transactionTriggerSaveSuccess" class="save-success">
                <i class="ri-checkbox-circle-fill"></i> 已儲存
              </span>
              <span v-if="transactionTriggerSaveError" class="save-error">
                <i class="ri-error-warning-line"></i> {{ transactionTriggerSaveError }}
              </span>
            </div>
          </div>
        </div>

        <div v-if="config" class="settings-card">
          <div class="settings-card-header">
            <i class="ri-user-add-line"></i>
            <div>
              <h2 class="settings-card-title">會員事件觸發</h2>
              <p class="settings-card-desc">
                控制 Wixtar 會員新建是否驅動本平台註冊禮。POS 與 LIFF 新註冊都會先寫入 Wixtar，CRM 首次建檔時統一由此觸發；LIFF 本機不再另發註冊禮。
              </p>
            </div>
          </div>

          <div class="card-body">
            <div v-if="!canEnableMemberEventTriggers" class="field-block webhook-prereq-warn">
              <p class="field-hint">
                <i class="ri-error-warning-line"></i>
                目前
                <template v-if="!config.isActive">整合未啟用</template>
                <template v-if="!config.isActive && config.syncMembers === false">，且</template>
                <template v-if="config.syncMembers === false">「同步會員」已關閉</template>
                。請先到上方編輯設定開啟後，才能啟用下方註冊禮開關。
              </p>
            </div>

            <div class="field-block">
              <div class="toggle-row">
                <label class="toggle-switch" :class="{ disabled: !canEnableMemberEventTriggers }">
                  <input
                    type="checkbox"
                    v-model="memberEventTriggers.wixtarWebhookRegistrationGiftEnabled"
                    :disabled="!canEnableMemberEventTriggers"
                  />
                  <span class="slider"></span>
                </label>
                <span class="toggle-label">Wixtar 會員事件觸發註冊禮</span>
              </div>
              <p class="field-hint">
                開啟後，CRM 首次從 Wixtar 建檔會員時會依「註冊禮」活動發禮（Wixtar webhook 管道為 Wixtar，LIFF 新註冊管道為 LINE）。
                若 Wixtar 端已有註冊禮，或要用行銷活動做複雜註冊流程，請關閉此開關並停用本平台註冊禮活動。
              </p>
            </div>

            <div class="field-actions">
              <button class="btn btn-primary" :disabled="memberEventTriggerSaving" @click="handleSaveMemberEventTriggers">
                <i class="ri-save-line"></i>
                {{ memberEventTriggerSaving ? '儲存中...' : '儲存設定' }}
              </button>
              <span v-if="memberEventTriggerSaveSuccess" class="save-success">
                <i class="ri-checkbox-circle-fill"></i> 已儲存
              </span>
              <span v-if="memberEventTriggerSaveError" class="save-error">
                <i class="ri-error-warning-line"></i> {{ memberEventTriggerSaveError }}
              </span>
            </div>
          </div>
        </div>

        <div v-if="config" class="settings-card">
          <div class="settings-card-header">
            <i class="ri-alarm-warning-line"></i>
            <div>
              <h2 class="settings-card-title">票券到期提醒</h2>
              <p class="settings-card-desc">在票券到期前透過 LINE 提醒會員使用</p>
            </div>
          </div>

          <div class="card-body">
            <div class="field-block">
              <div class="toggle-row">
                <label class="toggle-switch">
                  <input type="checkbox" v-model="ticketReminder.ticketExpiryReminderEnabled" />
                  <span class="slider"></span>
                </label>
                <span class="toggle-label">啟用票券到期前提醒</span>
              </div>
              <p class="field-hint">
                啟用後，系統每天 09:30 自動推播 LINE 訊息給有即將到期票券的會員（僅推播給已加入好友且未封鎖的會員）。
              </p>
            </div>

            <div v-if="ticketReminder.ticketExpiryReminderEnabled" class="field-block">
              <label class="form-label">提前幾天提醒</label>
              <select v-model="ticketReminder.ticketExpiryReminderDays" class="form-control field-select">
                <option :value="1">到期前 1 天</option>
                <option :value="3">到期前 3 天</option>
                <option :value="7">到期前 7 天</option>
                <option :value="14">到期前 14 天</option>
              </select>
            </div>

            <div class="field-actions">
              <button class="btn btn-primary" :disabled="reminderSaving" @click="handleSaveTicketReminder">
                <i class="ri-save-line"></i>
                {{ reminderSaving ? '儲存中...' : '儲存設定' }}
              </button>
              <span v-if="reminderSaveSuccess" class="save-success">
                <i class="ri-checkbox-circle-fill"></i> 已儲存
              </span>
              <span v-if="reminderSaveError" class="save-error">
                <i class="ri-error-warning-line"></i> {{ reminderSaveError }}
              </span>
            </div>

            <div class="section-divider"></div>

            <div class="field-block">
              <label class="form-label">票券資料同步</label>
              <p class="field-hint">
                提醒功能掃描的是本地票券鏡像。日常變動由 Wixtar webhook 自動更新，
                但<strong>啟用前既有的票券不會有 webhook 事件</strong>，需在此手動同步一次；
                若懷疑有漏送也可重新同步修補。
              </p>

              <div class="sync-stats">
                <div class="info-item">
                  <span class="info-label">已鏡像票券</span>
                  <span class="info-value">{{ mirrorStats.totalTickets }} 張</span>
                </div>
                <div class="info-item">
                  <span class="info-label">涵蓋會員</span>
                  <span class="info-value">{{ mirrorStats.totalMembers }} 人</span>
                </div>
                <div class="info-item">
                  <span class="info-label">最後同步時間</span>
                  <span class="info-value">{{ formatDate(mirrorStats.lastSyncedAt) }}</span>
                </div>
              </div>

              <div v-if="latestSyncBatch" class="sync-batch">
                <div class="sync-batch-header">
                  <span :class="getSyncBatchBadgeClass(latestSyncBatch.status)">
                    {{ getSyncBatchStatusName(latestSyncBatch.status) }}
                  </span>
                  <span class="text-muted">
                    {{ latestSyncBatch.scope === 'all' ? '全部會員' : '可推播會員' }}
                    ．{{ formatDate(latestSyncBatch.createdAt) }}
                  </span>
                </div>
                <div class="sync-progress">
                  <div class="sync-progress-bar">
                    <div class="sync-progress-fill" :style="{ width: syncProgressPercent + '%' }"></div>
                  </div>
                  <span class="sync-progress-text">
                    {{ latestSyncBatch.processedMembers }} / {{ latestSyncBatch.totalMembers || 0 }} 位會員
                    <template v-if="syncRunning && !latestSyncBatch.enqueueDone">（掃描入隊中）</template>
                    ．成功 {{ latestSyncBatch.successMembers }}．失敗 {{ latestSyncBatch.failedMembers }}．
                    同步 {{ latestSyncBatch.syncedTickets }} 張票券
                  </span>
                </div>
                <p v-if="syncStuck" class="log-error">
                  <i class="ri-error-warning-line"></i>
                  超過 30 分鐘無進度，可視為卡住，可重新開始同步。
                </p>
                <p v-if="latestSyncBatch.errorMessage" class="log-error">
                  <i class="ri-error-warning-line"></i> {{ latestSyncBatch.errorMessage }}
                </p>
              </div>

              <div v-if="recentSyncBatches.length > 1" class="sync-history">
                <div class="sync-history-title">最近同步批次</div>
                <div
                  v-for="batch in recentSyncBatches"
                  :key="batch.id"
                  class="sync-history-item"
                >
                  <span :class="getSyncBatchBadgeClass(batch.status)">
                    {{ getSyncBatchStatusName(batch.status) }}
                  </span>
                  <span class="text-muted">
                    {{ batch.scope === 'all' ? '全部' : '可推播' }}
                    ．{{ batch.processedMembers }}/{{ batch.totalMembers || 0 }} 人
                    ．{{ batch.syncedTickets }} 張
                    ．{{ formatDate(batch.createdAt) }}
                  </span>
                </div>
              </div>
            </div>

            <div class="field-actions">
              <select v-model="syncScope" class="form-control field-select" :disabled="syncing || syncRunning">
                <option value="pushable">僅同步可推播的會員（建議）</option>
                <option value="all">同步全部已綁定會員</option>
              </select>
              <button class="btn btn-secondary" :disabled="syncing || syncRunning" @click="handleSyncTickets">
                <i class="ri-refresh-line"></i>
                {{ syncing ? '建立中...' : (syncRunning ? '同步進行中...' : (syncStuck ? '重新開始同步' : '開始同步')) }}
              </button>
              <span v-if="syncError" class="save-error">
                <i class="ri-error-warning-line"></i> {{ syncError }}
              </span>
            </div>
          </div>
        </div>

        <div v-if="config" class="settings-card">
          <div class="settings-card-header">
            <i class="ri-coin-line"></i>
            <div>
              <h2 class="settings-card-title">點數資料同步</h2>
              <p class="settings-card-desc">導入初期補齊餘額與歷史明細鏡像</p>
            </div>
          </div>
          <div class="card-body">
            <div class="field-block">
              <p class="field-hint">
                請<strong>先完成會員導入並綁定 Wixtar</strong>，再執行點數同步。
                Wixtar 無法一次拉取全部會員清單，本功能只會同步 CRM 內已綁定、可取 Token 的會員。
                日常異動之後仍依 webhook 更新；此處用於啟用前存量補齊或修補漏送。
              </p>

              <div class="sync-stats">
                <div class="info-item">
                  <span class="info-label">可同步會員</span>
                  <span class="info-value">{{ pointEligibleMembers }} 人</span>
                </div>
                <div class="info-item">
                  <span class="info-label">已鏡像明細</span>
                  <span class="info-value">{{ pointMirrorStats.totalLedgers }} 筆</span>
                </div>
                <div class="info-item">
                  <span class="info-label">涵蓋會員</span>
                  <span class="info-value">{{ pointMirrorStats.totalMembers }} 人</span>
                </div>
                <div class="info-item">
                  <span class="info-label">最後同步時間</span>
                  <span class="info-value">{{ formatDate(pointMirrorStats.lastSyncedAt) }}</span>
                </div>
              </div>

              <p v-if="pointEligibleMembers === 0" class="log-error">
                <i class="ri-error-warning-line"></i>
                目前沒有可同步的會員。請先完成會員導入與 Wixtar 綁定後再試。
              </p>

              <div v-if="latestPointSyncBatch" class="sync-batch">
                <div class="sync-batch-header">
                  <span :class="getSyncBatchBadgeClass(latestPointSyncBatch.status)">
                    {{ getSyncBatchStatusName(latestPointSyncBatch.status) }}
                  </span>
                  <span class="text-muted">
                    {{ latestPointSyncBatch.scope === 'all' ? '全部會員' : '可推播會員' }}
                    ．{{ formatDate(latestPointSyncBatch.createdAt) }}
                  </span>
                </div>
                <div class="sync-progress">
                  <div class="sync-progress-bar">
                    <div class="sync-progress-fill" :style="{ width: pointSyncProgressPercent + '%' }"></div>
                  </div>
                  <span class="sync-progress-text">
                    {{ latestPointSyncBatch.processedMembers }} / {{ latestPointSyncBatch.totalMembers || 0 }} 位會員
                    <template v-if="pointSyncRunning && !latestPointSyncBatch.enqueueDone">（掃描入隊中）</template>
                    ．成功 {{ latestPointSyncBatch.successMembers }}．失敗 {{ latestPointSyncBatch.failedMembers }}．
                    明細 {{ latestPointSyncBatch.syncedLedgers || 0 }} 筆
                  </span>
                </div>
                <p v-if="pointSyncStuck" class="log-error">
                  <i class="ri-error-warning-line"></i>
                  超過 30 分鐘無進度，可視為卡住，可重新開始同步。
                </p>
                <p v-if="latestPointSyncBatch.errorMessage" class="log-error">
                  <i class="ri-error-warning-line"></i> {{ latestPointSyncBatch.errorMessage }}
                </p>
              </div>

              <div v-if="recentPointSyncBatches.length > 1" class="sync-history">
                <div class="sync-history-title">最近同步批次</div>
                <div
                  v-for="batch in recentPointSyncBatches"
                  :key="batch.id"
                  class="sync-history-item"
                >
                  <span :class="getSyncBatchBadgeClass(batch.status)">
                    {{ getSyncBatchStatusName(batch.status) }}
                  </span>
                  <span class="text-muted">
                    {{ batch.scope === 'all' ? '全部' : '可推播' }}
                    ．{{ batch.processedMembers }}/{{ batch.totalMembers || 0 }} 人
                    ．{{ batch.syncedLedgers || 0 }} 筆
                    ．{{ formatDate(batch.createdAt) }}
                  </span>
                </div>
              </div>
            </div>

            <div class="field-actions">
              <select
                v-model="pointSyncScope"
                class="form-control field-select"
                :disabled="pointSyncing || pointSyncRunning"
              >
                <option value="pushable">僅同步可推播的會員（建議）</option>
                <option value="all">同步全部已綁定會員</option>
              </select>
              <button
                class="btn btn-secondary"
                :disabled="pointSyncing || pointSyncRunning || pointEligibleMembers === 0"
                @click="handleSyncPoints"
              >
                <i class="ri-refresh-line"></i>
                {{ pointSyncing ? '建立中...' : (pointSyncRunning ? '同步進行中...' : (pointSyncStuck ? '重新開始同步' : '開始同步')) }}
              </button>
              <span v-if="pointSyncError" class="save-error">
                <i class="ri-error-warning-line"></i> {{ pointSyncError }}
              </span>
            </div>
          </div>
        </div>

        <div v-if="config" class="settings-card">
          <div class="settings-card-header">
            <i class="ri-file-list-line"></i>
            <div>
              <h2 class="settings-card-title">同步記錄</h2>
              <p class="settings-card-desc">最近的 Wixtar 同步操作</p>
            </div>
            <div class="card-header-actions">
              <button class="btn btn-sm btn-secondary" @click="handleViewSyncLogs">
                <i class="ri-file-list-line"></i>
                查看全部
              </button>
            </div>
          </div>
          <div class="card-body">
            <div v-if="syncLogsLoading" class="loading-mini">
              <div class="spinner-mini"></div>
              <span>載入中...</span>
            </div>
            <div v-else-if="recentSyncLogs.length === 0" class="empty-mini">
              <p class="text-muted">尚無同步記錄</p>
            </div>
            <div v-else class="sync-logs-list">
              <div
                v-for="log in recentSyncLogs"
                :key="log.id"
                class="sync-log-item"
              >
                <div class="log-info">
                  <span class="log-type">{{ getSyncTypeName(log.syncType) }}</span>
                  <span class="log-direction">{{ getSyncDirectionName(log.syncDirection) }}</span>
                  <span :class="getStatusBadgeClass(log.syncStatus)">
                    {{ getStatusName(log.syncStatus) }}
                  </span>
                </div>
                <div class="log-meta">
                  <span class="log-time">{{ formatDate(log.createdAt) }}</span>
                  <span v-if="log.errorMessage" class="log-error">
                    <i class="ri-error-warning-line"></i>
                    {{ log.errorMessage }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </template>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { wixtarService } from '@/api/modules/wixtar.js';
import { clientService } from '@/api/modules/clients.js';
import { clientSettingsService, NAMESPACES } from '@/api/modules/clientSettings.js';
import authService from '@/helpers/authservice/authService.js';
import { showError } from '@/utils/swal.js';

/** 同步進行中時的進度輪詢間隔 */
const SYNC_POLL_INTERVAL_MS = 5000;

export default {
  name: 'WixtarPanel',
  setup() {
    const router = useRouter();

    const loading = ref(false);
    const error = ref(null);
    const config = ref(null);
    const syncLogsLoading = ref(false);
    const recentSyncLogs = ref([]);
    const clientMemberSystemType = ref(null);

    const fetchConfig = async () => {
      loading.value = true;
      error.value = null;

      try {
        const response = await wixtarService.getConfig();

        if (response && response.success === true) {
          config.value = response.data;
          if (config.value) {
            await fetchRecentSyncLogs();
          }
        } else if (response && response.id && response.platformId) {
          config.value = response;
          await fetchRecentSyncLogs();
        } else {
          config.value = null;
        }
      } catch (err) {
        if (err.response?.status !== 404) {
          error.value = err.message || '獲取配置失敗';
          await showError('載入失敗', error.value);
        } else {
          config.value = null;
        }
      } finally {
        loading.value = false;
      }
    };

    const fetchRecentSyncLogs = async () => {
      syncLogsLoading.value = true;
      try {
        const response = await wixtarService.getSyncLogs({
          page: 1,
          pageSize: 5
        });
        if (response && response.success && response.data) {
          recentSyncLogs.value = response.data.items || [];
        }
      } catch (err) {
        console.error('獲取同步記錄失敗:', err);
      } finally {
        syncLogsLoading.value = false;
      }
    };

    const getSyncTypeName = (type) => {
      const types = {
        member: '會員',
        order: '訂單',
        points: '點數',
        ticket: '票券'
      };
      return types[type] || type;
    };

    const getSyncDirectionName = (direction) => {
      const directions = {
        wixtar_to_crm: 'Wixtar → CRM',
        crm_to_wixtar: 'CRM → Wixtar'
      };
      return directions[direction] || direction;
    };

    const getStatusName = (status) => {
      const statuses = {
        success: '成功',
        failed: '失敗',
        conflict: '衝突'
      };
      return statuses[status] || status;
    };

    const getStatusBadgeClass = (status) => {
      const classes = {
        success: 'badge badge-success',
        failed: 'badge badge-danger',
        conflict: 'badge badge-warning'
      };
      return classes[status] || 'badge badge-secondary';
    };

    const formatDate = (dateString) => {
      if (!dateString) return '-';
      return new Date(dateString).toLocaleString('zh-TW');
    };

    const handleCreateConfig = () => {
      if (clientMemberSystemType.value === 'LOCKLINE') {
        showError('無法設定', '此客戶使用 LOCKLINE 會員系統，無法設定 Wixtar 整合。請先將客戶的會員系統類型設定為 WIXTAR。');
        return;
      }
      router.push({ name: 'ClientWixtarConfigEdit' });
    };

    const handleEditConfig = () => {
      if (clientMemberSystemType.value === 'LOCKLINE') {
        showError('無法編輯', '此客戶使用 LOCKLINE 會員系統，無法編輯 Wixtar 整合。請先將客戶的會員系統類型設定為 WIXTAR。');
        return;
      }
      router.push({ name: 'ClientWixtarConfigEdit' });
    };

    const handleViewSyncLogs = () => {
      router.push({ name: 'ClientWixtarSyncLogs' });
    };

    // ─── 交易事件觸發（升等／集章）───────────────────
    // 前置條件只在設定介面把關（整合啟用 + 同步訂單），事件路徑不再重複查 config
    const canEnableTransactionTriggers = computed(() => {
      if (!config.value) return false;
      if (!config.value.isActive) return false;
      // null 視為開啟，與後端 webhook _checkSyncFlag 一致
      if (config.value.syncOrders === false) return false;
      return true;
    });

    const canEnableMemberEventTriggers = computed(() => {
      if (!config.value) return false;
      if (!config.value.isActive) return false;
      if (config.value.syncMembers === false) return false;
      return true;
    });

    const transactionTriggers = ref({
      wixtarTransactionTierUpgradeEnabled: false,
      wixtarTransactionStampIssueEnabled: false
    });
    const transactionTriggerSaving = ref(false);
    const transactionTriggerSaveSuccess = ref(false);
    const transactionTriggerSaveError = ref('');

    const memberEventTriggers = ref({
      wixtarWebhookRegistrationGiftEnabled: false
    });
    const memberEventTriggerSaving = ref(false);
    const memberEventTriggerSaveSuccess = ref(false);
    const memberEventTriggerSaveError = ref('');

    const loadTransactionTriggerSettings = async () => {
      try {
        const [wixtarRes, tierRes] = await Promise.all([
          clientSettingsService.get(NAMESPACES.WIXTAR),
          clientSettingsService.get(NAMESPACES.MEMBER_TIER)
        ]);
        const wixtar = wixtarRes?.data?.settings ?? {};
        const tier = tierRes?.data?.settings ?? {};
        // wixtar 為主；舊版暫存在 member_tier 的值以 OR 相容
        transactionTriggers.value = {
          wixtarTransactionTierUpgradeEnabled: !!(
            wixtar.wixtarTransactionTierUpgradeEnabled || tier.wixtarTransactionTierUpgradeEnabled
          ),
          wixtarTransactionStampIssueEnabled: !!(
            wixtar.wixtarTransactionStampIssueEnabled || tier.wixtarTransactionStampIssueEnabled
          )
        };
        memberEventTriggers.value = {
          wixtarWebhookRegistrationGiftEnabled: !!wixtar.wixtarWebhookRegistrationGiftEnabled
        };
      } catch (err) {
        console.error('載入 Wixtar 交易觸發設定失敗:', err);
      }
    };

    const handleSaveTransactionTriggers = async () => {
      transactionTriggerSaving.value = true;
      transactionTriggerSaveSuccess.value = false;
      transactionTriggerSaveError.value = '';
      try {
        const wantTier = !!transactionTriggers.value.wixtarTransactionTierUpgradeEnabled;
        const wantStamp = !!transactionTriggers.value.wixtarTransactionStampIssueEnabled;
        if ((wantTier || wantStamp) && !canEnableTransactionTriggers.value) {
          transactionTriggerSaveError.value = '請先啟用 Wixtar 整合並開啟「同步訂單」，才能啟用升等／集章';
          // 強制關閉 UI 狀態，避免顯示已勾選卻無法生效
          transactionTriggers.value.wixtarTransactionTierUpgradeEnabled = false;
          transactionTriggers.value.wixtarTransactionStampIssueEnabled = false;
          return;
        }
        const payload = {
          wixtarTransactionTierUpgradeEnabled: wantTier,
          wixtarTransactionStampIssueEnabled: wantStamp
        };
        await clientSettingsService.merge(NAMESPACES.WIXTAR, payload);
        // 清掉舊版暫存在 member_tier 的同名開關，避免 OR 相容讀取殘留
        await clientSettingsService.merge(NAMESPACES.MEMBER_TIER, {
          wixtarTransactionTierUpgradeEnabled: false,
          wixtarTransactionStampIssueEnabled: false
        });
        transactionTriggerSaveSuccess.value = true;
        setTimeout(() => { transactionTriggerSaveSuccess.value = false; }, 3000);
      } catch (err) {
        transactionTriggerSaveError.value = err?.response?.data?.message ?? '儲存失敗，請稍後再試';
      } finally {
        transactionTriggerSaving.value = false;
      }
    };

    const handleSaveMemberEventTriggers = async () => {
      memberEventTriggerSaving.value = true;
      memberEventTriggerSaveSuccess.value = false;
      memberEventTriggerSaveError.value = '';
      try {
        const wantGift = !!memberEventTriggers.value.wixtarWebhookRegistrationGiftEnabled;
        if (wantGift && !canEnableMemberEventTriggers.value) {
          memberEventTriggerSaveError.value = '請先啟用 Wixtar 整合並開啟「同步會員」，才能啟用註冊禮';
          memberEventTriggers.value.wixtarWebhookRegistrationGiftEnabled = false;
          return;
        }
        await clientSettingsService.merge(NAMESPACES.WIXTAR, {
          wixtarWebhookRegistrationGiftEnabled: wantGift
        });
        memberEventTriggerSaveSuccess.value = true;
        setTimeout(() => { memberEventTriggerSaveSuccess.value = false; }, 3000);
      } catch (err) {
        memberEventTriggerSaveError.value = err?.response?.data?.message ?? '儲存失敗，請稍後再試';
      } finally {
        memberEventTriggerSaving.value = false;
      }
    };

    // ─── 票券到期提醒 ───────────────────────────────
    const ticketReminder = ref({
      ticketExpiryReminderEnabled: false,
      ticketExpiryReminderDays: 3
    });
    const reminderSaving = ref(false);
    const reminderSaveSuccess = ref(false);
    const reminderSaveError = ref('');

    const loadTicketReminderSettings = async () => {
      try {
        const res = await clientSettingsService.get(NAMESPACES.WIXTAR);
        const s = res?.data?.settings ?? {};
        ticketReminder.value = {
          ticketExpiryReminderEnabled: s.ticketExpiryReminderEnabled ?? false,
          ticketExpiryReminderDays: s.ticketExpiryReminderDays ?? 3
        };
      } catch (err) {
        console.error('載入票券到期提醒設定失敗:', err);
      }
    };

    const handleSaveTicketReminder = async () => {
      reminderSaving.value = true;
      reminderSaveSuccess.value = false;
      reminderSaveError.value = '';

      try {
        // 用 merge，避免覆寫同命名空間的交易觸發開關
        await clientSettingsService.merge(NAMESPACES.WIXTAR, {
          ticketExpiryReminderEnabled: !!ticketReminder.value.ticketExpiryReminderEnabled,
          ticketExpiryReminderDays: ticketReminder.value.ticketExpiryReminderDays
        });
        reminderSaveSuccess.value = true;
        setTimeout(() => { reminderSaveSuccess.value = false; }, 3000);
      } catch (err) {
        reminderSaveError.value = err?.response?.data?.message ?? '儲存失敗，請稍後再試';
      } finally {
        reminderSaving.value = false;
      }
    };

    // ─── 票券鏡像同步 ───────────────────────────────
    const SYNC_STALE_MS = 30 * 60 * 1000;
    const syncScope = ref('pushable');
    const syncing = ref(false);
    const syncError = ref('');
    const mirrorStats = ref({ totalTickets: 0, totalMembers: 0, lastSyncedAt: null });
    const latestSyncBatch = ref(null);
    const recentSyncBatches = ref([]);
    let syncPollTimer = null;

    const isBatchActiveStatus = (batch) => (
      ['pending', 'processing'].includes(batch?.status)
    );

    const isBatchHeartbeatStale = (batch) => {
      if (!batch || !isBatchActiveStatus(batch)) return false;
      const heartbeat = batch.lastHeartbeatAt || batch.updatedAt || batch.createdAt;
      if (!heartbeat) return false;
      return Date.now() - new Date(heartbeat).getTime() > SYNC_STALE_MS;
    };

    // 進行中且心跳仍有效 → 禁用重跑；卡住則允許重新開始
    const syncRunning = computed(() => (
      isBatchActiveStatus(latestSyncBatch.value) && !isBatchHeartbeatStale(latestSyncBatch.value)
    ));

    const syncStuck = computed(() => isBatchHeartbeatStale(latestSyncBatch.value));

    const syncProgressPercent = computed(() => {
      const batch = latestSyncBatch.value;
      if (!batch || !batch.totalMembers) {
        return batch && isBatchActiveStatus(batch) ? 5 : 0;
      }
      return Math.min(100, Math.round((batch.processedMembers / batch.totalMembers) * 100));
    });

    const getSyncBatchStatusName = (status) => {
      const names = {
        pending: '等待中',
        processing: '同步中',
        completed: '已完成',
        failed: '失敗'
      };
      return names[status] || status;
    };

    const getSyncBatchBadgeClass = (status) => {
      const classes = {
        pending: 'badge badge-secondary',
        processing: 'badge badge-warning',
        completed: 'badge badge-success',
        failed: 'badge badge-danger'
      };
      return classes[status] || 'badge badge-secondary';
    };

    const stopSyncPolling = () => {
      if (syncPollTimer) {
        clearInterval(syncPollTimer);
        syncPollTimer = null;
      }
    };

    const loadSyncStatus = async () => {
      try {
        const res = await wixtarService.getTicketMirrorSyncStatus();
        const data = res?.data ?? {};
        mirrorStats.value = data.stats ?? { totalTickets: 0, totalMembers: 0, lastSyncedAt: null };
        latestSyncBatch.value = data.latestBatch ?? null;
        recentSyncBatches.value = data.recentBatches ?? (data.latestBatch ? [data.latestBatch] : []);

        // 同步結束或卡住就停止輪詢，避免無謂的請求
        if (!syncRunning.value) {
          stopSyncPolling();
        }
      } catch (err) {
        console.error('載入票券同步狀態失敗:', err);
      }
    };

    const startSyncPolling = () => {
      stopSyncPolling();
      syncPollTimer = setInterval(loadSyncStatus, SYNC_POLL_INTERVAL_MS);
    };

    const handleSyncTickets = async () => {
      syncing.value = true;
      syncError.value = '';

      try {
        await wixtarService.syncTicketMirror(syncScope.value);
        await loadSyncStatus();
        if (syncRunning.value) {
          startSyncPolling();
        }
      } catch (err) {
        syncError.value = err?.response?.data?.message ?? '建立同步任務失敗，請稍後再試';
      } finally {
        syncing.value = false;
      }
    };

    // ─── 點數鏡像同步 ───────────────────────────────
    const pointSyncScope = ref('pushable');
    const pointSyncing = ref(false);
    const pointSyncError = ref('');
    const pointEligibleMembers = ref(0);
    const pointMirrorStats = ref({ totalLedgers: 0, totalMembers: 0, lastSyncedAt: null });
    const latestPointSyncBatch = ref(null);
    const recentPointSyncBatches = ref([]);
    let pointSyncPollTimer = null;

    const pointSyncRunning = computed(() => (
      isBatchActiveStatus(latestPointSyncBatch.value)
        && !isBatchHeartbeatStale(latestPointSyncBatch.value)
    ));

    const pointSyncStuck = computed(() => isBatchHeartbeatStale(latestPointSyncBatch.value));

    const pointSyncProgressPercent = computed(() => {
      const batch = latestPointSyncBatch.value;
      if (!batch || !batch.totalMembers) {
        return batch && isBatchActiveStatus(batch) ? 5 : 0;
      }
      return Math.min(100, Math.round((batch.processedMembers / batch.totalMembers) * 100));
    });

    const stopPointSyncPolling = () => {
      if (pointSyncPollTimer) {
        clearInterval(pointSyncPollTimer);
        pointSyncPollTimer = null;
      }
    };

    const loadPointSyncStatus = async () => {
      try {
        const res = await wixtarService.getPointMirrorSyncStatus();
        const data = res?.data ?? {};
        pointEligibleMembers.value = data.eligibleMembers ?? 0;
        pointMirrorStats.value = data.stats ?? {
          totalLedgers: 0,
          totalMembers: 0,
          lastSyncedAt: null
        };
        latestPointSyncBatch.value = data.latestBatch ?? null;
        recentPointSyncBatches.value = data.recentBatches
          ?? (data.latestBatch ? [data.latestBatch] : []);

        if (!pointSyncRunning.value) {
          stopPointSyncPolling();
        }
      } catch (err) {
        console.error('載入點數同步狀態失敗:', err);
      }
    };

    const startPointSyncPolling = () => {
      stopPointSyncPolling();
      pointSyncPollTimer = setInterval(loadPointSyncStatus, SYNC_POLL_INTERVAL_MS);
    };

    const handleSyncPoints = async () => {
      pointSyncing.value = true;
      pointSyncError.value = '';

      try {
        await wixtarService.syncPointMirror(pointSyncScope.value);
        await loadPointSyncStatus();
        if (pointSyncRunning.value) {
          startPointSyncPolling();
        }
      } catch (err) {
        pointSyncError.value = err?.response?.data?.message ?? '建立同步任務失敗，請稍後再試';
      } finally {
        pointSyncing.value = false;
      }
    };

    const loadClientInfo = async () => {
      const currentUser = authService.getCurrentUser();
      if (!currentUser?.clientId) {
        return;
      }

      try {
        const response = await clientService.getClient(currentUser.clientId);
        if (response && response.success && response.data?.clientData) {
          clientMemberSystemType.value = response.data.clientData.memberSystemType || 'LOCKLINE';
        }
      } catch (err) {
        console.error('載入客戶資訊失敗:', err);
      }
    };

    onMounted(async () => {
      await loadClientInfo();
      if (clientMemberSystemType.value !== 'LOCKLINE') {
        await fetchConfig();
        if (config.value) {
          await Promise.all([
            loadTransactionTriggerSettings(),
            loadTicketReminderSettings(),
            loadSyncStatus(),
            loadPointSyncStatus()
          ]);
          if (syncRunning.value) {
            startSyncPolling();
          }
          if (pointSyncRunning.value) {
            startPointSyncPolling();
          }
        }
      }
    });

    onUnmounted(() => {
      stopSyncPolling();
      stopPointSyncPolling();
    });

    return {
      loading,
      error,
      config,
      syncLogsLoading,
      recentSyncLogs,
      clientMemberSystemType,
      fetchConfig,
      getSyncTypeName,
      getSyncDirectionName,
      getStatusName,
      getStatusBadgeClass,
      formatDate,
      handleCreateConfig,
      handleEditConfig,
      handleViewSyncLogs,
      canEnableTransactionTriggers,
      canEnableMemberEventTriggers,
      transactionTriggers,
      transactionTriggerSaving,
      transactionTriggerSaveSuccess,
      transactionTriggerSaveError,
      handleSaveTransactionTriggers,
      memberEventTriggers,
      memberEventTriggerSaving,
      memberEventTriggerSaveSuccess,
      memberEventTriggerSaveError,
      handleSaveMemberEventTriggers,
      ticketReminder,
      reminderSaving,
      reminderSaveSuccess,
      reminderSaveError,
      handleSaveTicketReminder,
      syncScope,
      syncing,
      syncError,
      syncRunning,
      syncStuck,
      syncProgressPercent,
      mirrorStats,
      latestSyncBatch,
      recentSyncBatches,
      getSyncBatchStatusName,
      getSyncBatchBadgeClass,
      handleSyncTickets,
      pointSyncScope,
      pointSyncing,
      pointSyncError,
      pointEligibleMembers,
      pointMirrorStats,
      latestPointSyncBatch,
      recentPointSyncBatches,
      pointSyncRunning,
      pointSyncStuck,
      pointSyncProgressPercent,
      handleSyncPoints
    };
  }
};
</script>

<style scoped>
.wixtar-panel {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.settings-card {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  overflow: hidden;
  max-width: 760px;
}

.settings-card-title {
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 4px;
  color: #2d3748;
}

.settings-card-desc {
  font-size: 13px;
  color: #6c757d;
  margin: 0;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #6c757d;
}

.spinner {
  width: 36px;
  height: 36px;
  border: 3px solid #e9ecef;
  border-top-color: #667eea;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-bottom: 12px;
}

.alert {
  border-radius: 4px;
  margin: 0;
  padding: 16px;
  background-color: #fff3cd;
  border-left: 4px solid #ffc107;
}

.alert-inner {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}

.alert-inner i {
  font-size: 20px;
  color: #856404;
  margin-top: 2px;
}

.alert-inner strong {
  color: #856404;
}

.alert-inner p {
  margin: 8px 0 0;
  color: #856404;
}

.settings-card-header {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 24px;
  background: #f8f9ff;
  border-bottom: 1px solid #e9ecef;
}

.settings-card-header > i {
  font-size: 32px;
  color: #667eea;
  margin-top: 2px;
  flex-shrink: 0;
}

.settings-card-header > div:nth-child(2) {
  flex: 1;
  min-width: 0;
}

.card-header-actions {
  margin-left: auto;
  flex-shrink: 0;
}

.card-body {
  padding: 24px;
}

.status-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 16px;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.info-label {
  font-size: 12px;
  color: #6c757d;
}

.info-value {
  font-size: 14px;
  font-weight: 500;
}

.text-success {
  color: #28a745;
}

.text-danger {
  color: #dc3545;
}

.text-muted {
  color: #6c757d;
}

.sync-logs-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.sync-log-item {
  padding: 12px;
  background: #f8f9fa;
  border-radius: 4px;
  border-left: 3px solid #007bff;
}

.log-info {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.log-type {
  font-weight: 600;
  font-size: 14px;
}

.log-direction {
  font-size: 12px;
  color: #6c757d;
}

.log-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  color: #6c757d;
}

.log-error {
  color: #dc3545;
  display: flex;
  align-items: center;
  gap: 4px;
}

.empty-state {
  text-align: center;
  padding: 40px 20px;
}

.empty-content {
  max-width: 400px;
  margin: 0 auto;
}

.empty-icon {
  font-size: 48px;
  color: #6c757d;
  margin-bottom: 12px;
}

.empty-state h3 {
  margin-bottom: 8px;
}

.loading-mini {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 20px;
  justify-content: center;
}

.spinner-mini {
  border: 2px solid #f3f3f3;
  border-top: 2px solid #007bff;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.empty-mini {
  text-align: center;
  padding: 20px;
}

.badge {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.badge-success {
  background-color: #d4edda;
  color: #155724;
}

.badge-danger {
  background-color: #f8d7da;
  color: #721c24;
}

.badge-warning {
  background-color: #fff3cd;
  color: #856404;
}

.badge-secondary {
  background-color: #e2e3e5;
  color: #383d41;
}

.error-state {
  text-align: center;
  padding: 40px 20px;
}

.error-message {
  color: #dc3545;
  margin-bottom: 12px;
}

/* ─── 票券到期提醒與鏡像同步 ─────────────────── */

.field-block {
  margin-bottom: 20px;
}

.field-block:last-of-type {
  margin-bottom: 12px;
}

.toggle-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.toggle-label {
  font-size: 14px;
  font-weight: 500;
  color: #2d3748;
}

/* .toggle-switch 定義於各頁面本地，此處與客戶設定頁保持一致的外觀 */
.toggle-switch {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
  cursor: pointer;
  flex-shrink: 0;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
  position: absolute;
}

.slider {
  position: absolute;
  inset: 0;
  background: #dee2e6;
  border-radius: 24px;
  transition: background 0.2s;
}

.slider::before {
  content: '';
  position: absolute;
  width: 18px;
  height: 18px;
  left: 3px;
  top: 3px;
  background: #fff;
  border-radius: 50%;
  transition: transform 0.2s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.toggle-switch input:checked + .slider {
  background: #667eea;
}

.toggle-switch input:checked + .slider::before {
  transform: translateX(20px);
}

.field-hint {
  margin: 8px 0 0;
  font-size: 13px;
  line-height: 1.6;
  color: #6c757d;
}

.webhook-prereq-warn .field-hint {
  color: #856404;
  background: #fff3cd;
  border: 1px solid #ffeeba;
  border-radius: 6px;
  padding: 10px 12px;
  margin-top: 0;
}

.webhook-prereq-warn .field-hint i {
  margin-right: 4px;
  vertical-align: -1px;
}

.toggle-switch.disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.field-select {
  max-width: 260px;
}

.field-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.save-success {
  font-size: 13px;
  color: #28a745;
}

.save-error {
  font-size: 13px;
  color: #dc3545;
}

.section-divider {
  height: 1px;
  background: #e9ecef;
  margin: 24px 0;
}

.sync-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
  margin-top: 12px;
}

.sync-batch {
  margin-top: 16px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 6px;
}

.sync-batch-header {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  font-size: 13px;
  margin-bottom: 10px;
}

.sync-progress-bar {
  height: 6px;
  background: #e9ecef;
  border-radius: 3px;
  overflow: hidden;
}

.sync-progress-fill {
  height: 100%;
  background: #667eea;
  transition: width 0.3s;
}

.sync-progress-text {
  display: block;
  margin-top: 8px;
  font-size: 12px;
  color: #6c757d;
}

.sync-history {
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid #e9ecef;
}

.sync-history-title {
  font-size: 13px;
  font-weight: 600;
  color: #495057;
  margin-bottom: 8px;
}

.sync-history-item {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  font-size: 12px;
  padding: 4px 0;
}

@media (max-width: 768px) {
  .field-select {
    max-width: 100%;
    width: 100%;
  }
}
</style>
